import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import JobCard from '../components/JobCard';
import { FaBookmark, FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';

const SavedJobs = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [savedJobs, setSavedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      toast.error('Please login to view saved jobs');
      navigate('/login');
      return;
    }
    fetchSavedJobs();
  }, [isAuthenticated, navigate]);

  const fetchSavedJobs = async () => {
    try {
      const { data } = await API.get('/users/saved-jobs');
      setSavedJobs(data);
    } catch (error) {
      console.error('Error fetching saved jobs:', error);
      toast.error('Failed to load saved jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsave = (jobId) => {
    setSavedJobs(savedJobs.filter(job => job._id !== jobId));
    fetchSavedJobs(); // Refresh the list
  };

  const filteredJobs = savedJobs.filter(job =>
    job.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.company?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    job.location?.city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-t-2 border-[#00c2ab]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 font-sans pb-20">
      {/* Premium Dashboard Header */}
      <div className="relative bg-gradient-to-br from-[#00c2ab] to-[#009688] pt-16 pb-32 overflow-hidden">
        {/* Abstract Background Blurs */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 rounded-full bg-white opacity-10 blur-3xl mix-blend-overlay"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-72 h-72 rounded-full bg-white opacity-10 blur-2xl mix-blend-overlay"></div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-3 tracking-tight drop-shadow-md">Saved Jobs</h1>
          <p className="text-lg text-teal-50 font-medium">Keep track of the opportunities you love</p>
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-20 relative z-20">
        <div className="max-w-6xl mx-auto">
          {/* Floating Control Center */}
          <div className="bg-white rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.1)] p-6 md:p-8 mb-10 border border-gray-100">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-6">
              <div className="flex items-center gap-4 w-full md:w-auto">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl flex items-center justify-center shadow-inner border border-teal-200 shrink-0">
                  <FaBookmark className="text-[#00c2ab] text-2xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-extrabold text-gray-900 leading-tight">Your Collection</h2>
                  <p className="text-gray-500 font-medium mt-1">
                    {savedJobs.length} {savedJobs.length === 1 ? 'job' : 'jobs'} saved
                  </p>
                </div>
              </div>
            </div>

            {/* Search */}
            {savedJobs.length > 0 && (
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-5 flex items-center pointer-events-none">
                  <FaSearch className="text-gray-400 group-focus-within:text-[#00c2ab] transition-colors" />
                </div>
                <input
                  type="text"
                  placeholder="Search your saved jobs by title, company, or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-6 py-4 bg-gray-50 border-2 border-gray-100 rounded-2xl text-gray-900 placeholder-gray-400 font-medium focus:bg-white focus:ring-4 focus:ring-teal-50 focus:border-[#00c2ab] transition-all duration-300 shadow-inner"
                />
              </div>
            )}
          </div>

          {/* Jobs List */}
          {filteredJobs.length > 0 ? (
            <div className="space-y-8">
              {filteredJobs.map(job => (
                <div key={job._id} className="transform transition-all duration-300 hover:-translate-y-1">
                  <JobCard
                    job={job}
                    isSaved={true}
                    onSave={handleUnsave}
                  />
                </div>
              ))}
            </div>
          ) : savedJobs.length > 0 ? (
            <div className="bg-white rounded-[2rem] shadow-xl p-16 text-center border border-gray-100 transform transition-all hover:-translate-y-1 hover:shadow-2xl">
              <div className="w-24 h-24 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                <FaSearch className="text-4xl text-[#00c2ab]" />
              </div>
              <h3 className="text-3xl font-extrabold text-gray-900 mb-3">No matching jobs found</h3>
              <p className="text-xl text-gray-500 max-w-md mx-auto">
                We couldn't find any saved jobs matching "{searchQuery}". Try adjusting your search terms.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-[2rem] shadow-xl p-16 text-center border border-gray-100 transform transition-all hover:-translate-y-1 hover:shadow-2xl">
              <div className="w-24 h-24 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                <FaBookmark className="text-4xl text-[#00c2ab]" />
              </div>
              <h3 className="text-3xl font-extrabold text-gray-900 mb-3">No saved jobs yet</h3>
              <p className="text-xl text-gray-500 mb-8 max-w-md mx-auto">
                You haven't saved any jobs yet. Start exploring and save the opportunities you're interested in!
              </p>
              <button
                onClick={() => navigate('/jobs')}
                className="inline-block bg-gradient-to-r from-[#00c2ab] to-[#009688] text-white px-10 py-4 rounded-xl font-bold shadow-[0_10px_25px_rgba(0,194,171,0.3)] hover:shadow-[0_15px_35px_rgba(0,194,171,0.4)] transform hover:-translate-y-1 transition-all duration-300"
              >
                Browse Available Jobs
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SavedJobs;
